import { useState } from "react";
import axios, { AxiosResponse } from "axios";

interface ExportState {
	[key: string]: boolean;
}

interface CsvExportHook {
	exportCsv: (endpoint: string, payload: any, exportKey?: string) => Promise<void>;
	isExporting: (exportKey?: string) => boolean;
}

const useCsvExport = (firebaseApp: any): CsvExportHook => {
	const [isExporting, setIsExporting] = useState<ExportState>({});
	
	// Construct base URL from Firebase project ID
	const projectId = firebaseApp?.options?.projectId
	const baseURL = `https://us-central1-${projectId}.cloudfunctions.net`;

	async function getIdToken(app: any): Promise<string> {
		const user = app.auth().currentUser;
		if (!user) throw new Error("Not signed in");
		return await user.getIdToken();
	}

	async function downloadFile(response: AxiosResponse): Promise<void> {
		const contentDisposition = response.headers['content-disposition'];
		let serverFilename = 'export.csv'; // fallback filename
		
		if (contentDisposition) {
			const filenameMatch = contentDisposition.match(/filename="(.+)"/);
			if (filenameMatch) {
				serverFilename = filenameMatch[1];
			}
		}

		const blob = new Blob([response.data], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.style.display = "none";
		a.href = url;
		a.download = serverFilename;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	}

	async function exportCsv(
		endpoint: string,
		payload: any,
		exportKey: string = "default"
	): Promise<void> {
		if (isExporting[exportKey]) return;

		setIsExporting(prev => ({ ...prev, [exportKey]: true }));
		try {
			const token = await getIdToken(firebaseApp);
			const api = axios.create({
				baseURL: baseURL,
				timeout: 60000,
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
			});

			const response = await api.post(endpoint, payload);
			await downloadFile(response);
		} finally {
			setIsExporting(prev => ({ ...prev, [exportKey]: false }));
		}
	}

	return {
		exportCsv,
		isExporting: (exportKey: string = "default"): boolean => isExporting[exportKey] || false,
	};
};

export default useCsvExport;
